import type { Dayjs } from 'dayjs';

type DisabledDate = (current: Dayjs, info: any) => boolean;

/**
 * Panel năm/tháng chỉ nên bị disable khi TOÀN BỘ khoảng thời gian của ô đó
 * không hợp lệ, nếu không cả năm sẽ bị chặn dù trong năm vẫn còn ngày chọn được.
 *
 * 연도/월 셀은 해당 기간 전체가 선택 불가일 때만 비활성화합니다. 그렇지 않으면
 * 그 해에 선택 가능한 날짜가 남아 있어도 연도 전체가 막혀 버립니다.
 */
export const relaxDisabledDate = <T extends DisabledDate | undefined>(
	disabledDate: T,
	enabled = true
): T => {
	if (!disabledDate || !enabled) return disabledDate;

	return ((current: Dayjs, info: any) => {
		const type = info?.type;

		if (type === 'year' || type === 'month') {
			return (
				disabledDate(current.startOf(type), info) &&
				disabledDate(current.endOf(type), info)
			);
		}

		return disabledDate(current, info);
	}) as T;
};

/**
 * Sau khi đổi năm/tháng, nếu ngày cũ rơi vào vùng bị disable thì lấy ngày
 * hợp lệ đầu tiên trong tháng đó.
 *
 * 연도/월을 바꾼 뒤 기존 일자가 선택 불가 구간에 들어가면 해당 월에서
 * 선택 가능한 첫 번째 날짜를 사용합니다.
 */
export const createAllowedDateResolver =
	(disabledDate?: DisabledDate) =>
	(candidate: Dayjs): Dayjs | null => {
		if (!disabledDate) return candidate;

		if (!disabledDate(candidate, { type: 'date' } as any)) return candidate;

		const daysInMonth = candidate.daysInMonth();

		for (let day = 1; day <= daysInMonth; day += 1) {
			const next = candidate.date(day);

			if (!disabledDate(next, { type: 'date' } as any)) return next;
		}

		return null;
	};
